import type { ReactNode } from 'react';
import { useCallback } from 'react';


import { Button } from '@twilio-paste/core/button';
import { Form } from '@twilio-paste/core/form';
import type { PopoverStateReturnProps } from '@twilio-paste/core/popover';
import { Formik } from 'formik';
import type { FormikProps, FormikConfig, FormikValues } from 'formik';

import { Drawer } from '../Drawer';
import { FormDrawerErrorState } from '../FormDrawer/FormDrawerErrorState';
import { FormDrawerLoadingState } from '../FormDrawer/FormDrawerLoadingState';

export type ReadOnlyDrawerDisplayProps = {
  state: PopoverStateReturnProps;
  modal?: boolean;
  onClose?: () => void;
};

interface OwnProps<T> {
  title: string;
  subtitle?: string;
  isLoading?: boolean;
  isError?: boolean;
  children: (form: FormikProps<T>) => NonNullable<ReactNode>;
}

export type ReadOnlyFormDrawerProps<T> = ReadOnlyDrawerDisplayProps &
  Pick<FormikConfig<T>, 'initialValues'> &
  OwnProps<T>;

export function ReadOnlyFormDrawer<T extends FormikValues>({
  modal,
  title,
  subtitle,
  ...rest
}: ReadOnlyFormDrawerProps<T>): JSX.Element {
  return (
    <Drawer aria-label={title} state={rest.state} modal={modal}>
      <Drawer.Header title={title} subtitle={subtitle} />

      <ReadOnlyFromDrawerContent {...rest} />
    </Drawer>
  );
}

type ReadOnlyFromDrawerContentProps<T> = Omit<
  ReadOnlyFormDrawerProps<T>,
  'modal' | 'title' | 'subtitle'
>;

function ReadOnlyFromDrawerContent<T extends FormikValues>({
  children,
  state,
  isError,
  isLoading,
  ...rest
}: ReadOnlyFromDrawerContentProps<T>) {
  const handleDismiss = useCallback(() => {
    state.hide();
  }, [state]);

  if (isLoading) {
    return <FormDrawerLoadingState />;
  }

  if (isError) {
    return <FormDrawerErrorState />;
  }

  return (
    <Formik {...rest} onSubmit={() => {}}>
      {form => (
        <>
          <Drawer.Content>
            <Form>{children(form)}</Form>
          </Drawer.Content>
          <Drawer.Footer>
            <Button variant="secondary" onClick={handleDismiss}>
              Close
            </Button>
          </Drawer.Footer>
        </>
      )}
    </Formik>
  );
}
